/** The faction racial: one charge, one decision, one battle.
 *
 * `armyPerk.ts` already carries twelve faction perks and every one of them is
 * passive - it reads the board and pays out on its own, so an army's faction
 * changes what happens to it and never what its owner may do. H5's racials are
 * the other kind: a button with a charge on it. This file is that kind, and it
 * is deliberately one file with one charge rather than three special cases
 * scattered through the turn machine.
 *
 * The price, which is the same for every faction and is what keeps this from
 * being a grab bag:
 *
 *   - one charge per side per battle (`BattleHeroState.racialUsed`);
 *   - the active stack's whole turn;
 *   - and no permanent army out of any of them. A gated duplicate is
 *     `F_SUMMONED`, so it is never folded back into the army when the battle
 *     ends: what the gate buys is this fight. The one faction allowed to make
 *     army permanently is Gravewatch, which already does it through Gravecalling,
 *     where a skill rate-limits it.
 *
 * A side with no hero has no charge, because the charge lives on the hero. That
 * is not a special case so much as the same rule every other hero-scoped thing
 * here follows - a wandering monster casts no spells either.
 *
 * Adding a faction is a data edit: `FactionDef.racial` names the kind and its
 * numbers, and the switch below is the closed union over those kinds. No rule
 * in this file names a faction (D9).
 */

import type { CreatureDefId } from "../ids.js";
import { asModifierSrcId } from "../ids.js";
import { BP_ONE, FP_ONE, roundHalfAway } from "../numeric.js";
import type { Reason } from "../commands.js";
import { Err, Ok, type Result } from "../result.js";
import type { FactionDef } from "../content/registry.js";
import { type ContentRegistry, must } from "../content/registry.js";
import {
  BF_HEXES, F_NON_LIVING, F_SHOOTER, F_TWO_HEX, F_WAR_MACHINE,
  type BattleState, hexNeighbours, isAlive,
} from "./state.js";
import { F2_GATED, F_ACTED, F_CLONE } from "./flags.js";
import { activeStack } from "./order.js";
import { sideFaction } from "./armyPerk.js";
import { addStack, freeHexFor } from "./summon.js";
import { CONTENT_FLAG_MASK, flagsForCreature, shotsForCreature } from "./creature.js";
import { gainRunes } from "./runes.js";
import { floodHex } from "./wet.js";
import { RESIST_STAT } from "./stats.js";
import { type Ctx, addMod, damageStack, effectiveMaxHp, healStack } from "./apply.js";
import { jSetField, jSetI32 } from "./journal.js";

export const SRC_RAGE = asModifierSrcId("racial.rage");
export const SRC_SWIFT = asModifierSrcId("racial.swiftness");
export const SRC_ATTUNE = asModifierSrcId("racial.attune");
export const SRC_PLAGUE = asModifierSrcId("racial.plague");



/** The racial this side may spend, or null - no hero, no faction, mixed army. */
export function racialOf(
  s: BattleState, content: ContentRegistry, side: 0 | 1,
): NonNullable<FactionDef["racial"]> | null {
  if (s.heroes[side].id === null || s.heroes[side].racialUsed) return null;
  return sideFaction(s, content, side)?.racial ?? null;
}

/**
 * Stacks a racial may be spent on. Always the active stack and only the active
 * stack: the racial costs a turn, and the only turn a side can spend is the one
 * it is having.
 */
function eligible(s: BattleState, side: 0 | 1, stack: number): boolean {
  if (stack !== activeStack(s)) return false;
  if (!isAlive(s.stacks, stack)) return false;
  if (s.stacks.side[stack] !== side) return false;
  if ((s.stacks.flags[stack]! & F_ACTED) !== 0) return false;
  return true;
}

/** How many of a side's stacks have already fallen. Phantoms and machines are
 *  not casualties: a Clone that lapsed was never there and a broken catapult is
 *  equipment. */
function fallenOnSide(s: BattleState, side: 0 | 1): number {
  const st = s.stacks;
  let n = 0;
  for (let i = 0; i < st.n; i++) {
    if (st.side[i] !== side || isAlive(st, i)) continue;
    if ((st.flags[i]! & (F_CLONE | F_WAR_MACHINE)) !== 0) continue;
    if ((st.flags2[i]! & F2_GATED) !== 0) continue;
    n++;
  }
  return n;
}

export function validateRacial(
  s: BattleState, content: ContentRegistry, side: 0 | 1, stack: number,
): Result<NonNullable<FactionDef["racial"]>, Reason> {
  const racial = racialOf(s, content, side);
  if (racial === null) return Err({ code: "TARGET_INVALID" });
  if (!eligible(s, side, stack)) return Err({ code: "STACK_NOT_ACTIVE", stack });
  const st = s.stacks;
  switch (racial.kind) {
    case "gate": {
      // Equipment does not gate, and neither does a phantom or a duplicate: a
      // gate that could gate a gate is an army that doubles every turn.
      if ((st.flags[stack]! & (F_WAR_MACHINE | F_CLONE)) !== 0) return Err({ code: "TARGET_INVALID" });
      if ((st.flags2[stack]! & F2_GATED) !== 0) return Err({ code: "TARGET_INVALID" });
      if (gateCount(st.count[stack]!, racial.pctBp) <= 0) return Err({ code: "TARGET_INVALID" });
      const twoHex = (st.flags[stack]! & F_TWO_HEX) !== 0;
      if (freeHexFor(s, side, st.hex[stack]!, twoHex) < 0) return Err({ code: "TARGET_INVALID" });
      return Ok(racial);
    }
    case "ritual": {
      if ((st.flags[stack]! & F_WAR_MACHINE) !== 0) return Err({ code: "TARGET_INVALID" });
      // Never the last creature of a stack: a ritual that could wipe out what
      // it is cast on is a way to lose a battle by pressing a button.
      if (ritualCost(st.count[stack]!, racial.costPctBp) <= 0) return Err({ code: "TARGET_INVALID" });
      return Ok(racial);
    }
    case "train": {
      // Nothing to promote a machine, a phantom or a top-tier creature into.
      if ((st.flags[stack]! & (F_WAR_MACHINE | F_CLONE)) !== 0) return Err({ code: "TARGET_INVALID" });
      if (upgradeOf(content, st.def[stack]!) === null) return Err({ code: "TARGET_INVALID" });
      return Ok(racial);
    }
    case "press": {
      // Nothing to re-arm: offering it to a line with no shooter in it would be
      // a button that visibly does nothing.
      if (!sideHasShooterOutOfShots(s, side)) return Err({ code: "TARGET_INVALID" });
      return Ok(racial);
    }
    case "overhaul": {
      if (!sideHasMechanical(s, side)) return Err({ code: "TARGET_INVALID" });
      return Ok(racial);
    }
    case "raise": {
      if (content.creatures.get(racial.creature) === undefined) {
        return Err({ code: "NO_SUCH_ENTITY", id: racial.creature as string });
      }
      if (raiseCount(s, side, racial.perKill, racial.cap) <= 0) return Err({ code: "TARGET_INVALID" });
      return Ok(racial);
    }
    case "channel": {
      // A hero who has not cast has nothing to be handed back, and the mana
      // alone is not worth the turn - so it is refused rather than wasted.
      if (!s.heroes[side].castThisRound) return Err({ code: "TARGET_INVALID" });
      return Ok(racial);
    }
    case "rage":
    case "swiftness":
    case "plague":
    case "attune":
    case "runegift":
    case "flood":
      return Ok(racial);
  }
}

/** The creature this one upgrades INTO, or null. Content states the reverse
 *  relation (`upgradeOf`), so this is the scan that inverts it. */
function upgradeOf(content: ContentRegistry, def: CreatureDefId): CreatureDefId | null {
  for (const c of content.creatures.values()) {
    if (c.upgradeOf === def) return c.id;
  }
  return null;
}

function sideHasShooterOutOfShots(s: BattleState, side: 0 | 1): boolean {
  const st = s.stacks;
  for (let i = 0; i < st.n; i++) {
    if (!isAlive(st, i) || st.side[i] !== side) continue;
    if ((st.flags[i]! & F_SHOOTER) === 0) continue;
    if (st.shots[i]! <= 0) return true;
  }
  return false;
}

function sideHasMechanical(s: BattleState, side: 0 | 1): boolean {
  const st = s.stacks;
  for (let i = 0; i < st.n; i++) {
    if (!isAlive(st, i) || st.side[i] !== side) continue;
    if ((st.flags[i]! & (F_NON_LIVING | F_WAR_MACHINE)) !== 0) return true;
  }
  return false;
}

/** What Gravewatch's raising is worth right now: what this side has killed. */
export function raiseCount(s: BattleState, side: 0 | 1, perKill: number, cap: number): number {
  const st = s.stacks;
  let killed = 0;
  for (let i = 0; i < st.n; i++) {
    if (st.side[i] !== side) continue;
    killed += st.killed[i]!;
  }
  const n = Math.floor(killed * perKill);
  return n > cap ? cap : n;
}

/** The duplicate's head count: a share of the original, at least one. */
export function gateCount(count: number, pctBp: number): number {
  const n = Math.floor((count * pctBp) / BP_ONE);
  return n < 1 ? (count >= 2 ? 1 : 0) : n;
}

/** What the ritual takes, and never the whole stack. */
export function ritualCost(count: number, costPctBp: number): number {
  if (count <= 1) return 0;
  const n = Math.floor((count * costPctBp) / BP_ONE);
  const capped = n < 1 ? 1 : n;
  return capped >= count ? count - 1 : capped;
}

/**
 * Spend it. Returns the stack whose turn was spent so the caller can end that
 * turn, which is the same contract `castHeroSpell` and `heroStrike` have.
 */
export function spendRacial(
  c: Ctx, content: ContentRegistry, side: 0 | 1, stack: number,
): Result<number, Reason> {
  const r = validateRacial(c.s, content, side, stack);
  if (!r.ok) return r;
  const racial = r.value;
  const s = c.s;
  const st = s.stacks;

  jSetField(c.j, s.heroes[side] as unknown as object, "racialUsed", true);

  switch (racial.kind) {
    case "gate": {
      const count = gateCount(st.count[stack]!, racial.pctBp);
      const twoHex = (st.flags[stack]! & F_TWO_HEX) !== 0;
      const at = freeHexFor(s, side, st.hex[stack]!, twoHex);
      // The duplicate copies the stack as it stands, wounds and buffs aside:
      // its numbers come off the store rather than off the creature def, so a
      // gate called up under Battle Fury brings a Bloodlusted copy. Modifiers are
      // deliberately NOT copied - a spell was cast on one stack, not on two.
      const made = addStack(c, content, {
        def: st.def[stack]!, side, count, hex: at,
        extraFlags2: F2_GATED,
        stats: {
          attack: st.attack[stack]!, defense: st.defense[stack]!,
          damageMin: st.damageMin[stack]!, damageMax: st.damageMax[stack]!,
          maxHp: st.maxHp[stack]!, morale: st.morale[stack]!, luck: st.luck[stack]!,
        },
      });
      if (made < 0) return Err({ code: "TARGET_INVALID" });
      c.events.push({
        type: "BattleStackSummoned", stack: made, creature: st.def[stack]!, count,
      });
      return Ok(stack);
    }
    case "rage": {
      const fallen = fallenOnSide(s, side);
      const raw = racial.attack + racial.perFallen * fallen;
      const gain = raw > racial.cap ? racial.cap : raw;
      if (gain > 0) {
        for (let i = 0; i < st.n; i++) {
          if (!isAlive(st, i) || st.side[i] !== side) continue;
          addMod(c, i, {
            source: SRC_RAGE, stat: "attack", op: "add", value: gain,
            stackGroup: SRC_RAGE as string, stackRule: "highest",
            expires: { kind: "battle" },
          });
        }
      }
      return Ok(stack);
    }
    /**
     * Castle. The stack fights on as its upgraded form.
     *
     * The def, the stats, the shots and the content half of the flags all move
     * together - a Master Bowyer that kept a Spearwarden's flags would not be a shooter -
     * and the bookkeeping half is preserved, or a stack could buy a second turn
     * by being trained after it had acted.
     *
     * The ARMY keeps what it brought. `foldSide` writes survivor counts against
     * the army's own slot defs and never reads the battle stack's, so this buys
     * one battle rather than a free upgrade, which is what makes it a racial
     * rather than an economy.
     */
    case "train": {
      const into = upgradeOf(content, st.def[stack]!);
      if (into === null) return Err({ code: "TARGET_INVALID" });
      const def = content.creatures.get(into);
      if (def === undefined) return Err({ code: "NO_SUCH_ENTITY", id: into as string });
      const oldMax = effectiveMaxHp(s, stack);
      const whole = st.topHp[stack]! >= oldMax;
      jSetField(c.j, st.def as unknown as Record<number, unknown>, stack, def.id);
      jSetI32(c.j, st.attack, stack, def.attack);
      jSetI32(c.j, st.defense, stack, def.defense);
      jSetI32(c.j, st.damageMin, stack, def.damageMin);
      jSetI32(c.j, st.damageMax, stack, def.damageMax);
      jSetI32(c.j, st.speed, stack, def.speed);
      jSetI32(c.j, st.maxHp, stack, def.hp);
      jSetI32(c.j, st.shots, stack, shotsForCreature(def));
      // A creature at full strength comes out of it at full strength; a wounded
      // one keeps its wound rather than being healed by the promotion.
      jSetI32(c.j, st.topHp, stack, whole ? def.hp : Math.min(st.topHp[stack]!, def.hp));
      jSetI32(c.j, st.flags, stack,
        (st.flags[stack]! & ~CONTENT_FLAG_MASK) | flagsForCreature(def));
      c.events.push({
        type: "BattleStackSummoned", stack, creature: def.id, count: st.count[stack]!,
      });
      return Ok(stack);
    }

    case "swiftness": {
      for (let i = 0; i < st.n; i++) {
        if (!isAlive(st, i) || st.side[i] !== side) continue;
        addMod(c, i, {
          source: SRC_SWIFT, stat: "speed", op: "add", value: racial.speed,
          stackGroup: SRC_SWIFT as string, stackRule: "highest",
          expires: { kind: "battle" },
        });
      }
      return Ok(stack);
    }

    /**
     * Tower. The mana AND the round's cast, handed back.
     *
     * Clearing `castThisRound` is the whole racial: the mana is the smaller
     * half. One charge a battle means at most one double-cast in a fight, which
     * is what keeps it from being a second spellbook.
     */
    case "channel": {
      const hero = s.heroes[side];
      const back = racial.manaPerKnowledge * hero.knowledge;
      jSetField(c.j, hero as unknown as object, "mana", hero.mana + back);
      jSetField(c.j, hero as unknown as object, "castThisRound", false);
      return Ok(stack);
    }

    case "raise": {
      const count = raiseCount(s, side, racial.perKill, racial.cap);
      if (count <= 0) return Err({ code: "TARGET_INVALID" });
      const raised = content.creatures.get(racial.creature);
      if (raised === undefined) {
        return Err({ code: "NO_SUCH_ENTITY", id: racial.creature as string });
      }
      const twoHex = raised.abilities.some((a) => a.kind === "TWO_HEX");
      const at = freeHexFor(s, side, st.hex[stack]!, twoHex);
      if (at < 0) return Err({ code: "TARGET_INVALID" });
      const made = addStack(c, content, { def: racial.creature, side, count, hex: at });
      if (made < 0) return Err({ code: "TARGET_INVALID" });
      c.events.push({
        type: "BattleStackSummoned", stack: made, creature: racial.creature, count,
      });
      return Ok(stack);
    }

    /**
     * Fenmire. Poison on every enemy stack, priced as a share of what each one
     * can take rather than a flat number, so it bites a Manyhead as hard as a
     * Bog Raider. `stackRule: "sum"` is the POISON ability's own, so a second dose
     * would stack the way a second bite does - there is only ever one here.
     */
    case "plague": {
      for (let i = 0; i < st.n; i++) {
        if (!isAlive(st, i) || st.side[i] === side) continue;
        if ((st.flags[i]! & F_WAR_MACHINE) !== 0) continue;
        const bite = Math.max(1, roundHalfAway((effectiveMaxHp(s, i) * racial.hpPctBp) / BP_ONE));
        addMod(c, i, {
          source: SRC_PLAGUE, stat: "hp", op: "add", value: -bite,
          stackGroup: SRC_PLAGUE as string, stackRule: "sum",
          expires: { kind: "battle" },
        });
      }
      return Ok(stack);
    }

    case "attune": {
      for (let i = 0; i < st.n; i++) {
        if (!isAlive(st, i) || st.side[i] !== side) continue;
        addMod(c, i, {
          source: SRC_ATTUNE, stat: RESIST_STAT.all, op: "max",
          value: Math.min(racial.resistFp, FP_ONE),
          stackGroup: SRC_ATTUNE as string, stackRule: "highest",
          expires: { kind: "round", rounds: racial.rounds },
        });
      }
      return Ok(stack);
    }

    case "press": {
      for (let i = 0; i < st.n; i++) {
        if (!isAlive(st, i) || st.side[i] !== side) continue;
        if ((st.flags[i]! & F_SHOOTER) === 0) continue;
        // Never above what the creature carries: a quiver is a quiver.
        const full = shotsForCreature(must(content.creatures, st.def[i]!, "creature"));
        const want = st.shots[i]! + racial.shots;
        jSetI32(c.j, st.shots, i, want > full ? full : want);
      }
      return Ok(stack);
    }

    case "overhaul": {
      for (let i = 0; i < st.n; i++) {
        if (!isAlive(st, i) || st.side[i] !== side) continue;
        if ((st.flags[i]! & (F_NON_LIVING | F_WAR_MACHINE)) === 0) continue;
        // Mends, never raises: `resurrectTo` is the living count.
        healStack(c, i, racial.heal, st.count[i]!);
        if (racial.shots > 0 && st.shots[i]! > 0) {
          jSetI32(c.j, st.shots, i, st.shots[i]! + racial.shots);
        }
      }
      return Ok(stack);
    }

    case "runegift": {
      for (let i = 0; i < st.n; i++) {
        if (!isAlive(st, i) || st.side[i] !== side) continue;
        gainRunes(c, content, i, racial.levels);
      }
      return Ok(stack);
    }

    /**
     * Blackwater. More of the board goes under.
     *
     * Written straight onto the mask rather than through a spell, because that
     * is what it is - and a dry board has no mask at all, so one is made. The
     * hexes chosen are the ones AROUND this side's own line, which is what makes
     * it a decision about where to stand rather than a free board-wide buff.
     */
    case "flood": {
      const wet = s.wet === undefined ? new Uint8Array(BF_HEXES) : Uint8Array.from(s.wet);
      let left = racial.hexes;
      const nb: number[] = [0, 0, 0, 0, 0, 0];
      for (let i = 0; i < st.n && left > 0; i++) {
        if (!isAlive(st, i) || st.side[i] !== side) continue;
        const here = st.hex[i]!;
        if (wet[here] !== 1) { floodHex(wet, here); left -= 1; }
        const k = hexNeighbours(here, nb);
        for (let m = 0; m < k && left > 0; m++) {
          if (wet[nb[m]!] === 1) continue;
          floodHex(wet, nb[m]!);
          left -= 1;
        }
      }
      jSetField(c.j, s as unknown as object, "wet", wet);
      return Ok(stack);
    }

    case "ritual": {
      const given = ritualCost(st.count[stack]!, racial.costPctBp);
      const hp = given * effectiveMaxHp(s, stack);
      const per = racial.hpPerMana < 1 ? 1 : racial.hpPerMana;
      const mana = Math.ceil(hp / per);
      const r2 = damageStack(c, stack, hp);
      c.events.push({
        type: "BattleAttacked", attacker: stack, target: stack,
        damage: r2.dealt, killed: r2.killed, ranged: false, retaliation: false,
      });
      const hero = s.heroes[side];
      jSetField(c.j, hero as unknown as object, "mana", hero.mana + mana);
      return Ok(stack);
    }
  }
}
